import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Banknote, HardHat, Loader2, ShieldAlert, Star, Timer } from 'lucide-react';
import { INTERVENTION_STATUTS, INTERVENTION_TYPES_DEMANDE } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import {
  EMPTY_REPORTING_FILTERS,
  ReportingFilters,
  type ReportingFilterValues,
} from '../../components/reporting/ReportingFilters';
import { ExportButtons } from '../../components/reporting/ExportButtons';
import { DonutChart } from '../../components/reporting/DonutChart';
import { defaultDateRange, toIsoFromDate, toIsoToDate } from '../../components/reporting/dateRange';
import {
  PRIORITE_BG,
  PRIORITE_HEX,
  STATUT_INTERVENTION_HEX,
  STATUT_INTERVENTION_LABEL,
  TYPE_DEMANDE_LABEL,
  formatCurrency,
  formatDurationH,
  formatNumber,
  formatPercent,
} from '../../components/reporting/format';
import type { InterventionsReportResult } from '../../components/reporting/types';

/**
 * Page `/reporting/interventions` (US-REP-04).
 *
 *  Filtres : service, dates, statut, priorité.
 *
 *  Sections :
 *   - Cards résumé (total + répartitions statut/priorité/type)
 *   - Délais : moyenne/médiane + barres par priorité
 *   - SLA : donut respectées vs breach + taux %
 *   - MTBF/MTTR top 20 équipements (table)
 *   - Satisfaction : note moyenne + distribution + top techniciens
 *   - Coûts par priorité + par service
 */
interface ServiceOption {
  id: string;
  code: string;
  nom_complet: string;
}

export function InterventionsReportPage() {
  const def = defaultDateRange();
  const [filters, setFilters] = useState<ReportingFilterValues>({
    ...EMPTY_REPORTING_FILTERS,
    date_from: def.from,
    date_to: def.to,
  });
  const [data, setData] = useState<InterventionsReportResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const printRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    api
      .get<{ data: ServiceOption[] }>('/services?statut=actif&page_size=200')
      .then((res) => setServices(res.data.data))
      .catch(() => setServices([]));
  }, []);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    const fromIso = toIsoFromDate(filters.date_from);
    const toIso = toIsoToDate(filters.date_to);
    if (fromIso) p.set('date_from', fromIso);
    if (toIso) p.set('date_to', toIso);
    if (filters.service_id) p.set('service_id', filters.service_id);
    if (filters.statut) p.set('statut', filters.statut);
    // Le backend filtre par `type` (type_demande) — on réutilise `type`.
    if (filters.type) p.set('type', filters.type);
    // Priorite n'est pas reconnu par le backend reporting filters, on n'envoie pas.
    return p;
  }, [filters]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.toString()]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const qs = queryParams.toString();
      const res = await api.get<{ data: InterventionsReportResult }>(
        `/reporting/interventions${qs ? `?${qs}` : ''}`,
      );
      setData(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <HardHat className="w-7 h-7 mr-2 text-primary-600" />
            Rapport Interventions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Délais, SLA, MTBF/MTTR, coûts et satisfaction.
          </p>
        </div>
        <ExportButtons
          excelUrl="/reporting/interventions.xlsx"
          excelParams={queryParams}
          filename="rapport-interventions"
          targetRef={printRef}
          pdfDisabled={!data}
        />
      </div>

      <ReportingFilters
        values={filters}
        onChange={setFilters}
        onRefresh={refresh}
        fields={{ dates: true, service: true, statut: true, type: true }}
        servicesOptions={services.map((s) => ({
          value: s.id,
          label: `${s.code} — ${s.nom_complet}`,
        }))}
        statutOptions={INTERVENTION_STATUTS.map((s) => ({
          value: s,
          label: STATUT_INTERVENTION_LABEL[s] ?? s,
        }))}
        typeOptions={INTERVENTION_TYPES_DEMANDE.map((t) => ({
          value: t,
          label: TYPE_DEMANDE_LABEL[t] ?? t,
        }))}
        typeLabel="Type demande"
      />

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center p-24 bg-white rounded-lg shadow-sm border border-gray-200">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
        </div>
      ) : data ? (
        <div ref={printRef} className="space-y-4">
          {/* Résumé */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard
              label="Total interventions"
              value={formatNumber(data.resume.total)}
              icon={HardHat}
              tone="primary"
            />
            <SummaryCard
              label="Durée moyenne"
              value={formatDurationH(data.delais.duree_moyenne_resolution)}
              icon={Timer}
              tone="purple"
            />
            <SummaryCard
              label="Coût total"
              value={formatCurrency(data.couts.cout_total)}
              icon={Banknote}
              tone="orange"
            />
            <SummaryCard
              label="Taux SLA respecté"
              value={formatPercent(data.sla.taux_respect_pct, 1)}
              icon={ShieldAlert}
              tone={
                data.sla.taux_respect_pct >= 90
                  ? 'green'
                  : data.sla.taux_respect_pct >= 70
                    ? 'orange'
                    : 'red'
              }
            />
          </div>

          {/* Répartitions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Panel title="Par statut">
              <DistList
                items={data.resume.par_statut.map((s) => ({
                  key: s.statut,
                  label: STATUT_INTERVENTION_LABEL[s.statut] ?? s.statut,
                  count: s.count,
                  color: STATUT_INTERVENTION_HEX[s.statut],
                }))}
              />
            </Panel>
            <Panel title="Par priorité">
              <DistList
                items={data.resume.par_priorite.map((p) => ({
                  key: p.priorite,
                  label: p.priorite,
                  count: p.count,
                  color: PRIORITE_HEX[p.priorite],
                }))}
              />
            </Panel>
            <Panel title="Par type de demande">
              <DistList
                items={data.resume.par_type.map((t) => ({
                  key: t.type,
                  label: TYPE_DEMANDE_LABEL[t.type] ?? t.type,
                  count: t.count,
                }))}
              />
            </Panel>
          </div>

          {/* Délais par priorité */}
          <Panel title="Délais de résolution par priorité">
            {data.delais.duree_par_priorite.length === 0 ? (
              <p className="text-xs text-gray-500 py-4 text-center">
                Aucune intervention clôturée sur la période.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <Th>Priorité</Th>
                      <Th right>Durée moyenne</Th>
                      <Th right>Durée max</Th>
                      <Th>Répartition</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.delais.duree_par_priorite.map((d) => {
                      const max = Math.max(
                        ...data.delais.duree_par_priorite.map((dd) => dd.duree_max),
                      );
                      const pct = max > 0 ? (d.duree_max / max) * 100 : 0;
                      const color = PRIORITE_BG[d.priorite] ?? 'bg-gray-400';
                      return (
                        <tr key={d.priorite}>
                          <td className="px-3 py-2 font-semibold">{d.priorite}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatDurationH(d.duree_moyenne)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatDurationH(d.duree_max)}
                          </td>
                          <td className="px-3 py-2 min-w-[200px]">
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${color}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="text-[11px] text-gray-500 mt-3">
              Durée médiane sur la période : {formatDurationH(data.delais.duree_mediane)}
            </p>
          </Panel>

          {/* SLA donut */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Respect SLA">
              <DonutChart
                data={[
                  {
                    key: 'respectees',
                    label: 'Respectées',
                    value: data.sla.respectees,
                    color: '#10b981',
                  },
                  {
                    key: 'breach',
                    label: 'En breach',
                    value: data.sla.breach,
                    color: '#ef4444',
                  },
                ]}
              />
              <div className="grid grid-cols-3 gap-2 mt-2 text-xs text-center">
                <div>
                  <p className="text-gray-500">Clôturées</p>
                  <p className="font-semibold text-gray-900">
                    {formatNumber(data.sla.total_cloturees)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Respectées</p>
                  <p className="font-semibold text-green-700">
                    {formatNumber(data.sla.respectees)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">En breach</p>
                  <p className="font-semibold text-red-700">{formatNumber(data.sla.breach)}</p>
                </div>
              </div>
            </Panel>

            <Panel title="Satisfaction utilisateurs">
              <div className="flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-yellow-400 fill-yellow-400 mr-2" />
                <p className="text-3xl font-bold text-gray-900">
                  {data.satisfaction.note_moyenne.toFixed(1)} / 5
                </p>
              </div>
              {data.satisfaction.note_distribution.length === 0 ? (
                <p className="text-xs text-gray-500 py-2 text-center">
                  Aucune évaluation sur la période.
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const item = data.satisfaction.note_distribution.find((n) => n.note === star);
                    const count = item?.count ?? 0;
                    const total = data.satisfaction.note_distribution.reduce(
                      (a, b) => a + b.count,
                      0,
                    );
                    const pct = total > 0 ? (count / total) * 100 : 0;
                    return (
                      <li key={star} className="flex items-center gap-2 text-xs">
                        <span className="w-6 inline-flex items-center text-gray-700">
                          {star}
                          <Star className="w-3 h-3 ml-0.5 text-yellow-500" />
                        </span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-yellow-400 rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="tabular-nums text-gray-600 w-14 text-right">
                          {formatNumber(count)} ({pct.toFixed(0)}%)
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </Panel>
          </div>

          {/* Top techniciens */}
          {data.satisfaction.top_techniciens.length > 0 && (
            <Panel title="Top techniciens (satisfaction)">
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.satisfaction.top_techniciens.map((t, idx) => (
                  <li
                    key={t.technicien_id}
                    className="flex items-center justify-between border border-gray-100 rounded-lg p-3 bg-gray-50"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {idx + 1}. {t.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatNumber(t.nb_interventions)} interventions
                      </p>
                    </div>
                    <span className="inline-flex items-center text-yellow-600 font-bold">
                      <Star className="w-4 h-4 mr-1 fill-yellow-400 text-yellow-400" />
                      {t.note_moyenne.toFixed(1)}
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {/* Coûts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel title="Coûts par priorité">
              {data.couts.cout_par_priorite.length === 0 ? (
                <p className="text-xs text-gray-500 py-4 text-center">Aucun coût enregistré.</p>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <Th>Priorité</Th>
                      <Th right>Total</Th>
                      <Th right>Moyen</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.couts.cout_par_priorite.map((c) => (
                      <tr key={c.priorite}>
                        <td className="px-3 py-2 font-semibold">{c.priorite}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatCurrency(c.cout_total)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatCurrency(c.cout_moyen)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <Panel title="Coûts par service">
              {data.couts.cout_par_service.length === 0 ? (
                <p className="text-xs text-gray-500 py-4 text-center">Aucun coût enregistré.</p>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <Th>Service</Th>
                      <Th right>Coût total</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.couts.cout_par_service.map((c) => (
                      <tr key={c.service_id}>
                        <td className="px-3 py-2 truncate max-w-[260px]" title={c.nom}>
                          {c.nom}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatCurrency(c.cout_total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>
          </div>

          {/* MTBF/MTTR */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900 inline-flex items-center">
                <Timer className="w-4 h-4 mr-2 text-primary-600" />
                MTBF / MTTR — top 20 équipements
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Code</Th>
                    <Th>Désignation</Th>
                    <Th right>MTBF (jours)</Th>
                    <Th right>MTTR (heures)</Th>
                    <Th right>Disponibilité</Th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {data.mtbf_mttr_par_equipement.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-500">
                        Pas assez de pannes pour calculer MTBF/MTTR sur la période.
                      </td>
                    </tr>
                  ) : (
                    data.mtbf_mttr_par_equipement.map((m) => (
                      <tr key={m.equipement_id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-medium text-primary-600 whitespace-nowrap">
                          {m.code}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-900 max-w-[300px] truncate"
                          title={m.designation}
                        >
                          {m.designation}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {m.mtbf_jours !== null ? formatNumber(m.mtbf_jours) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {m.mttr_heures !== null ? formatNumber(m.mttr_heures) : '—'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {m.disponibilite_pct !== null
                            ? formatPercent(m.disponibilite_pct, 2)
                            : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-[10px] text-gray-400 text-right pt-2">
            Généré le {new Date().toLocaleString('fr-FR')}
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500">Aucune donnée disponible.</p>
      )}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string;
  icon: typeof HardHat;
  tone: 'primary' | 'purple' | 'orange' | 'green' | 'red';
}) {
  const TONE: Record<typeof tone, string> = {
    primary: 'text-primary-600',
    purple: 'text-purple-600',
    orange: 'text-orange-600',
    green: 'text-green-600',
    red: 'text-red-600',
  };
  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <Icon className={`w-4 h-4 ${TONE[tone]}`} />
      </div>
      <p className={`text-2xl font-bold mt-1 ${TONE[tone]}`}>{value}</p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function DistList({
  items,
}: {
  items: Array<{ key: string; label: string; count: number; color?: string }>;
}) {
  const total = items.reduce((acc, it) => acc + it.count, 0);
  if (items.length === 0 || total === 0) {
    return <p className="text-xs text-gray-500 py-4 text-center">Aucune donnée.</p>;
  }
  return (
    <ul className="space-y-2">
      {items.map((it) => {
        const pct = total > 0 ? (it.count / total) * 100 : 0;
        return (
          <li key={it.key}>
            <div className="flex items-center justify-between text-xs mb-0.5">
              <span className="font-medium text-gray-700 truncate pr-2">{it.label}</span>
              <span className="text-gray-600 tabular-nums">
                {formatNumber(it.count)} · {pct.toFixed(0)}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  backgroundColor: it.color ?? '#3b82f6',
                }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider ${
        right ? 'text-right' : 'text-left'
      }`}
    >
      {children}
    </th>
  );
}
